import { Type } from 'class-transformer';
import {
  IsBoolean,
  IsDefined,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';
import { EntityStateSchemaDto } from '../../../../@base';
import { PropertyId } from '../../../../types';
import { UserDto } from '../../../users/user.dto';
import { PropertyApplicationStatus } from '../property-application/property-application.dto';
import { PropertyCharacteristicsDto, PropertyConstructionDto } from './property-characteristics.dto';
import { PropertyDefinitionDto } from './property-definition.dto';
import { PropertyDocumentDto } from './property-document.dto';
import { PropertyDpeDto } from './property-dpe.dto';
import { PropertyCreationStepSlug, PropertyVerificationStatus } from './property-enums';
import { PropertyFinancialsDto } from './property-financials.dto';
import { PropertyCreationGeospatialDto, PropertyGeospatialFeaturesDto } from './property-geospatial.dto';
import { PropertyKeyInfoDto } from './property-key-info.dto';
import { PropertyLocationDto } from './property-location.dto';
import { PropertyMandateDto } from './property-mandate.dto';
import { PropertyMarketStatusDto } from './property-market-status.dto';
import { PropertyMediaDto } from './property-media.dto';
import { PropertyMemberDto } from './property-member.dto';
import { PropertyModerationDto } from './property-moderation.dto';
import { PropertyPresentationDto } from './property-presentation.dto';

export class PropertyCreationDto {
  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyDefinitionDto)
  definition: PropertyDefinitionDto = new PropertyDefinitionDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyLocationDto)
  location: PropertyLocationDto = new PropertyLocationDto();

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyCreationGeospatialDto)
  geospatial?: PropertyCreationGeospatialDto;

  farthestStepReached: PropertyCreationStepSlug = 'definition';
}

export class PropertyApplicationInfoDto {
  @IsNumber()
  @IsDefined()
  acceptedCount = 0;

  @IsNumber()
  @IsDefined()
  appliedCount = 0;

  @IsBoolean()
  @IsDefined()
  accepting = true;

  @IsBoolean()
  @IsOptional()
  isApplied?: boolean;

  @IsEnum(PropertyApplicationStatus)
  @IsOptional()
  applicationStatus?: PropertyApplicationStatus;
}

export class PropertyVerificationDto {
  @IsEnum(PropertyVerificationStatus)
  @IsOptional()
  owner: PropertyVerificationStatus = PropertyVerificationStatus.UNVERIFIED;

  @IsOptional()
  @IsString()
  ownerRejectionReason?: string;

  @IsEnum(PropertyVerificationStatus)
  @IsOptional()
  dpe?: PropertyVerificationStatus;
}

export class PropertyDto extends EntityStateSchemaDto<PropertyId> {
  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyDefinitionDto)
  definition: PropertyDefinitionDto = new PropertyDefinitionDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyKeyInfoDto)
  keyInfo: PropertyKeyInfoDto = new PropertyKeyInfoDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyCharacteristicsDto)
  characteristics: PropertyCharacteristicsDto = new PropertyCharacteristicsDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyConstructionDto)
  construction: PropertyConstructionDto = new PropertyConstructionDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyDpeDto)
  dpe: PropertyDpeDto = new PropertyDpeDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyLocationDto)
  location: PropertyLocationDto = new PropertyLocationDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyFinancialsDto)
  financials: PropertyFinancialsDto = new PropertyFinancialsDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyMandateDto)
  mandate: PropertyMandateDto = new PropertyMandateDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyPresentationDto)
  presentation: PropertyPresentationDto = new PropertyPresentationDto();

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyMediaDto)
  media: PropertyMediaDto = new PropertyMediaDto();

  @IsOptional()
  @ValidateNested()
  @Type(() => UserDto)
  owner?: UserDto; // Populated field

  @IsOptional()
  @ValidateNested()
  @Type(() => UserDto)
  createdBy?: UserDto; // Populated field

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyGeospatialFeaturesDto)
  geospatial?: PropertyGeospatialFeaturesDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyApplicationInfoDto)
  application?: PropertyApplicationInfoDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyVerificationDto)
  verification: PropertyVerificationDto = new PropertyVerificationDto();

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyModerationDto)
  moderation?: PropertyModerationDto;

  @IsDefined()
  @ValidateNested()
  @Type(() => PropertyMarketStatusDto)
  marketStatus: PropertyMarketStatusDto = new PropertyMarketStatusDto();

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => PropertyDocumentDto)
  documents?: PropertyDocumentDto[] = [];

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => PropertyMemberDto)
  members?: PropertyMemberDto[] = [];

  farthestStepReached: PropertyCreationStepSlug = 'definition';
}

export class DraftPropertyListDto extends EntityStateSchemaDto<PropertyId> {
  @Type(() => PropertyDefinitionDto)
  definition!: PropertyDefinitionDto;

  @Type(() => PropertyLocationDto)
  location!: PropertyLocationDto;

  farthestStepReached!: PropertyCreationStepSlug;
}
